import React, { useState } from 'react';
import { Mail, Clock, FileText, MessageSquare, Book, ChevronDown, Send, CheckCircle2, AlertCircle, Menu } from 'lucide-react';
import { MainSidebar } from '../components/layout/MainSidebar';
import { Logo } from '../components/common/Logo';
import { supabase } from '../lib/supabase';
import { track } from '../utils/analytics';

interface FAQItemProps {
  question: string;
  answer: string;
  defaultOpen?: boolean;
}

const FAQItem: React.FC<FAQItemProps> = ({ question, answer, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
      >
        <span className="text-base font-semibold text-gray-900 pr-4">{question}</span>
        <ChevronDown
          className={`w-5 h-5 text-gray-600 flex-shrink-0 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>
      {isOpen && (
        <div className="px-6 pb-4 border-t border-gray-100">
          <p className="text-gray-700 leading-relaxed pt-3">{answer}</p>
        </div>
      )}
    </div>
  );
};

interface SupportPageProps {
  userEmail?: string | null;
  isAnonymous?: boolean;
  onSignIn?: () => void;
  onSignOut?: () => void;
}

export const SupportPage: React.FC<SupportPageProps> = ({
  userEmail = null,
  isAnonymous = false,
  onSignIn = () => {},
  onSignOut = () => {},
}) => {
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    issueType: 'general',
    subject: '',
    description: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isMainSidebarOpen, setIsMainSidebarOpen] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!contactForm.name.trim() || !contactForm.email.trim() || !contactForm.description.trim()) {
      setError('Please fill in all required fields.');
      return;
    }

    setIsSubmitting(true);
    track('support_request_submit_started', {
      issue_type: contactForm.issueType,
      has_subject: !!contactForm.subject?.trim(),
      is_anonymous: !!isAnonymous,
      page_path: typeof window !== 'undefined' ? window.location.pathname : undefined,
    });

    try {
      const apiUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/submit-support-request`;

      // Prefer authenticated user access token; fall back to anon key for guests.
      const { data: { session } } = await supabase.auth.getSession();
      const authToken = session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...contactForm,
          pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
          timestamp: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const message =
          (typeof errorData?.error === 'string' && errorData.error) ||
          (typeof errorData?.message === 'string' && errorData.message) ||
          `Failed to send message (HTTP ${response.status})`;
        setError(message);
        setIsSubmitting(false);

        track('support_request_submit_failed', {
          issue_type: contactForm.issueType,
          http_status: response.status,
          is_anonymous: !!isAnonymous,
        });
        return;
      }

      setSubmitted(true);
      setIsSubmitting(false);
      track('support_request_created', {
        issue_type: contactForm.issueType,
        is_anonymous: !!isAnonymous,
      });

      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitted(false);
        setContactForm({
          name: '',
          email: '',
          issueType: 'general',
          subject: '',
          description: '',
        });
      }, 3000);
    } catch (err) {
      console.error('Error submitting support request:', err);
      setError(err instanceof Error ? err.message : 'Failed to send message. Please try again.');
      setIsSubmitting(false);

      track('support_request_submit_failed', {
        issue_type: contactForm.issueType,
        is_anonymous: !!isAnonymous,
      });
    }
  };

  const faqs = [
    {
      question: 'How do I get started with CommsGenie?',
      answer: 'Create a free account, navigate to the Chat page, select a docket, and start asking questions. Check out our Quick Start Guide in the documentation for detailed steps.',
    },
    {
      question: 'How accurate are the AI responses?',
      answer: 'CommsGenie uses advanced AI models and provides citations to source documents. However, AI-generated responses should always be verified against the original documents, especially for legal purposes. We recommend using CommsGenie as a research assistant, not a replacement for reviewing primary sources.',
    },
    {
      question: 'What are my usage limits?',
      answer: 'Usage limits depend on your subscription tier. Free accounts get limited credits to try the service. Paid plans include monthly chat message allowances and document processing limits. Check the Pricing page for details or view your current usage in Account Settings.',
    },
    {
      question: 'Can I share my work with team members?',
      answer: 'Yes! Workspace collaboration features allow you to share analysis with team members. Create a workspace, invite collaborators, and work together on regulatory research projects.',
    },
    {
      question: 'Is my data secure?',
      answer: 'Yes. We take security seriously. All data is encrypted in transit and at rest. We do not share your data with third parties. See our Privacy Policy for complete details on how we protect your information.',
    },
    {
      question: 'Can I export my analysis?',
      answer: 'Export features are being enhanced. Currently, you can copy chat responses and citations. Advanced export options (PDF, Word, citation management integration) are coming in version 1.4.0.',
    },
  ];

  return (
    <div className="flex flex-col min-h-screen w-full bg-gray-50">
      {/* Main App Sidebar - Overlay */}
      <MainSidebar
        isOpen={isMainSidebarOpen}
        onClose={() => setIsMainSidebarOpen(false)}
        alwaysOverlay={true}
        userEmail={userEmail}
        isAnonymous={isAnonymous}
        onSignIn={onSignIn}
        onSignOut={onSignOut}
      />
      
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-gray-200 w-full">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setIsMainSidebarOpen(!isMainSidebarOpen)}
              className="p-2 rounded-lg hover:bg-gray-100"
              title="Toggle main menu"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <Logo variant="icon-only" size="sm" to="/" className="shrink-0" />
            <h1 className="text-xl font-bold text-gray-900">Help & Support</h1>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="max-w-5xl mx-auto py-12 px-4">
        {/* Hero section */}
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            How Can We Help You?
          </h2>
          <p className="text-lg text-gray-700">
            Find answers to common questions or contact our support team
          </p>
        </div>

        {/* Quick help cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <Mail className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Email Support</h3>
            <p className="text-gray-600 mb-4">
              Send us an email at{' '}
              <a
                href="mailto:support@commsgenie.com"
                className="text-green-600 hover:text-green-700 font-medium underline"
              >
                support@commsgenie.com
              </a>
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
              <Clock className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Response Time</h3>
            <p className="text-gray-600 mb-4">
              We typically respond within 24 hours during business days (Monday-Friday)
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <Book className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Documentation</h3>
            <p className="text-gray-600 mb-4">
              <a href="/docs" className="text-green-600 hover:text-green-700 font-medium underline">
                Browse our documentation
              </a>
              {' '}for guides and tutorials
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* FAQ Section */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h3>
            <div className="space-y-3">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  defaultOpen={index === 0}
                />
              ))}
            </div>

            <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h4 className="text-lg font-semibold text-blue-900 mb-2">
                Can't find what you're looking for?
              </h4>
              <p className="text-gray-700 mb-4">
                Check out our comprehensive documentation or submit a question using the contact form.
              </p>
              <div className="flex flex-wrap gap-3">
                <a
                  href="/docs"
                  className="inline-flex items-center px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  View Documentation
                </a>
                <a
                  href="/feedback"
                  className="inline-flex items-center px-4 py-2 bg-white text-blue-700 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors font-medium"
                >
                  <MessageSquare className="w-4 h-4 mr-2" />
                  Send Feedback
                </a>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h3 className="text-2xl font-bold text-gray-900 mb-6">Contact Support</h3>
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle2 className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h4>
                  <p className="text-gray-700">
                    We've received your request and will respond within 24 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-1">
                      Name *
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={contactForm.name}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, name: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-1">
                      Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={contactForm.email}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, email: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      required
                    />
                  </div>

                  <div>
                    <label htmlFor="issueType" className="block text-sm font-semibold text-gray-900 mb-1">
                      Issue Type
                    </label>
                    <select
                      id="issueType"
                      value={contactForm.issueType}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, issueType: e.target.value })
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="general">General Question</option>
                      <option value="technical">Technical Issue</option>
                      <option value="billing">Billing Question</option>
                      <option value="account">Account Issue</option>
                      <option value="feature">Feature Question</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="subject" className="block text-sm font-semibold text-gray-900 mb-1">
                      Subject
                    </label>
                    <input
                      id="subject"
                      type="text"
                      value={contactForm.subject}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, subject: e.target.value })
                      }
                      placeholder="Brief description of your issue"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label htmlFor="description" className="block text-sm font-semibold text-gray-900 mb-1">
                      Description *
                    </label>
                    <textarea
                      id="description"
                      value={contactForm.description}
                      onChange={(e) =>
                        setContactForm({ ...contactForm, description: e.target.value })
                      }
                      placeholder="Please provide as much detail as possible..."
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                      required
                    />
                  </div>

                  {error && (
                    <div className="flex items-center space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full flex items-center justify-center space-x-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Send Message</span>
                      </>
                    )}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>

        {/* Additional resources */}
        <div className="mt-12 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg p-8">
          <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">
            Additional Resources
          </h3>
          <div className="grid md:grid-cols-3 gap-6">
            <a
              href="/docs"
              className="flex flex-col items-center text-center p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
            >
              <Book className="w-8 h-8 text-green-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Documentation</h4>
              <p className="text-sm text-gray-600">Guides and tutorials</p>
            </a>

            <a
              href="/docs/releases"
              className="flex flex-col items-center text-center p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
            >
              <FileText className="w-8 h-8 text-blue-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Release Notes</h4>
              <p className="text-sm text-gray-600">Latest updates</p>
            </a>

            <a
              href="/feedback"
              className="flex flex-col items-center text-center p-4 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
            >
              <MessageSquare className="w-8 h-8 text-purple-600 mb-2" />
              <h4 className="font-semibold text-gray-900 mb-1">Share Feedback</h4>
              <p className="text-sm text-gray-600">Help us improve</p>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

